import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, downloadApiFile } from '../../../api/client';
import { formatClockLocationLabel } from '../../../utils/clockLocationDisplay';
import type { PayrollConnectorInfo, PayrollSettingsState } from './PayrollSettingsSection';

type PayrollTechnicianRow = {
  technicianId: string;
  technicianName: string;
  totalHours: number;
  approvedHours: number;
  pendingHours: number;
  rejectedHours: number;
  entryCount: number;
  isClockedIn: boolean;
  exceptions: string[];
};

type TimeEntryRow = {
  _id: string;
  technicianId: string;
  technicianName?: string;
  date?: string;
  hours?: number;
  kind?: 'manual' | 'clock';
  status?: 'submitted' | 'approved' | 'rejected';
  clockInAt?: string;
  clockOutAt?: string;
  clockInLocation?: { lat?: number; lng?: number; label?: string };
  clockOutLocation?: { lat?: number; lng?: number; label?: string };
  workOrderTitle?: string;
  siteName?: string;
  siteAddress?: string;
  payrollNote?: string;
  notes?: string;
  payrollExportStatus?: 'pending' | 'exported' | 'error' | 'skipped';
  payrollExportedAt?: string;
  payrollProvider?: string;
  payrollExportError?: string;
};

type PayrollSummary = {
  period: { from: string; to: string };
  technicians: PayrollTechnicianRow[];
  entries: TimeEntryRow[];
};

type ApprovalFilter = 'pending' | 'approved' | 'rejected' | 'all';

function startOfPayWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatWeekLabel(start: Date): string {
  const end = addDays(start, 6);
  return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatHours(value: number): string {
  return value.toFixed(2);
}

function formatWhen(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function locationLabel(
  loc?: { label?: string; lat?: number; lng?: number },
  siteFallback?: string
): string {
  const place = formatClockLocationLabel(loc, '');
  if (place) return place;
  if (siteFallback?.trim()) return siteFallback.trim();
  return 'Area unknown';
}

function mapsUrl(loc?: { lat?: number; lng?: number }): string | null {
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null;
  return `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
}

function isOpenEntry(entry: TimeEntryRow): boolean {
  return entry.kind === 'clock' && Boolean(entry.clockInAt) && !entry.clockOutAt;
}

function entryHours(entry: TimeEntryRow): number {
  if (isOpenEntry(entry) && entry.clockInAt) {
    return Math.max(0, (Date.now() - new Date(entry.clockInAt).getTime()) / 3600000);
  }
  return Number(entry.hours) || 0;
}

function statusClass(entry: TimeEntryRow): string {
  if (isOpenEntry(entry)) return 'hours-status hours-status--open';
  if (entry.status === 'approved') return 'hours-status hours-status--approved';
  if (entry.status === 'rejected') return 'hours-status hours-status--rejected';
  return 'hours-status hours-status--pending';
}

function statusLabel(entry: TimeEntryRow): string {
  if (isOpenEntry(entry)) return 'Open clock';
  return entry.status || 'submitted';
}

function toDateInputValue(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function toDatetimeLocalValue(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ManualEntryBadge(): React.JSX.Element {
  return <span className="hours-entry-kind">Added manually</span>;
}

type EntryFormMode = 'create' | 'edit';

type EntryFormState = {
  mode: EntryFormMode;
  entryId?: string;
  technicianId: string;
  date: string;
  hours: string;
  site: string;
  notes: string;
  clockInAt: string;
  clockOutAt: string;
  kind: 'manual' | 'clock';
};

function emptyEntryForm(technicianId = ''): EntryFormState {
  return {
    mode: 'create',
    technicianId,
    date: new Date().toISOString().slice(0, 10),
    hours: '',
    site: '',
    notes: '',
    clockInAt: '',
    clockOutAt: '',
    kind: 'manual'
  };
}

function entryToForm(entry: TimeEntryRow): EntryFormState {
  return {
    mode: 'edit',
    entryId: entry._id,
    technicianId: String(entry.technicianId),
    date: toDateInputValue(entry.date || entry.clockInAt),
    hours: String(entryHours(entry)),
    site: entry.workOrderTitle || entry.siteAddress || entry.siteName || '',
    notes: entry.notes || '',
    clockInAt: toDatetimeLocalValue(entry.clockInAt),
    clockOutAt: toDatetimeLocalValue(entry.clockOutAt),
    kind: entry.kind === 'clock' ? 'clock' : 'manual'
  };
}

function payrollExportLabel(entry: TimeEntryRow): string {
  if (entry.status !== 'approved') return '—';
  if (entry.payrollExportStatus === 'exported') {
    return entry.payrollProvider && entry.payrollProvider !== 'csv' ? 'Synced' : 'Exported';
  }
  if (entry.payrollExportStatus === 'error') return 'Sync error';
  return 'Not exported';
}

function payrollExportClass(entry: TimeEntryRow): string {
  if (entry.payrollExportStatus === 'exported') return 'hours-payroll-status hours-payroll-status--ok';
  if (entry.payrollExportStatus === 'error') return 'hours-payroll-status hours-payroll-status--error';
  return 'hours-payroll-status';
}

export default function HoursApprovalSection(): React.JSX.Element {
  const [weekStart, setWeekStart] = useState(() => startOfPayWeek(new Date()));
  const [summary, setSummary] = useState<PayrollSummary | null>(null);
  const [roster, setRoster] = useState<Array<{ _id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTechId, setSelectedTechId] = useState<string | 'all'>('all');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('pending');
  const [busy, setBusy] = useState<string | null>(null);
  const [entryForm, setEntryForm] = useState<EntryFormState | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettingsState | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  const weekIso = useMemo(() => weekStart.toISOString().slice(0, 10), [weekStart]);

  useEffect(() => {
    apiRequest<{ data: Array<{ _id: string; name: string }> }>('/api/users?role=technician')
      .then((res) => setRoster(res.data || []))
      .catch(() => setRoster([]));
  }, []);

  useEffect(() => {
    apiRequest<{
      settings: PayrollSettingsState;
      connectors: PayrollConnectorInfo[];
    }>('/api/settings/payroll')
      .then((res) => setPayrollSettings(res.settings))
      .catch(() => setPayrollSettings(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const techQuery = selectedTechId !== 'all' ? `&technicianId=${encodeURIComponent(selectedTechId)}` : '';
      const res = await apiRequest<{ data: PayrollSummary }>(
        `/api/time-entries/payroll?week=${encodeURIComponent(weekIso)}${techQuery}`
      );
      setSummary(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Failed to load hours.');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [weekIso, selectedTechId]);

  useEffect(() => {
    void load();
  }, [load]);

  const allEntries = useMemo(() => {
    if (!summary) return [];
    if (selectedTechId === 'all') return summary.entries;
    return summary.entries.filter((e) => String(e.technicianId) === selectedTechId);
  }, [summary, selectedTechId]);

  const pendingEntries = useMemo(
    () => allEntries.filter((e) => !isOpenEntry(e) && (e.status || 'submitted') === 'submitted'),
    [allEntries]
  );

  const filteredEntries = useMemo(() => {
    if (approvalFilter === 'all') return allEntries;
    if (approvalFilter === 'pending') return pendingEntries;
    if (approvalFilter === 'approved') {
      return allEntries.filter((e) => !isOpenEntry(e) && e.status === 'approved');
    }
    return allEntries.filter((e) => e.status === 'rejected');
  }, [allEntries, approvalFilter, pendingEntries]);

  const totals = useMemo(() => {
    const rows = summary?.technicians || [];
    return rows.reduce(
      (acc, row) => ({
        totalHours: acc.totalHours + row.totalHours,
        approvedHours: acc.approvedHours + row.approvedHours,
        pendingHours: acc.pendingHours + row.pendingHours
      }),
      { totalHours: 0, approvedHours: 0, pendingHours: 0 }
    );
  }, [summary]);

  async function reviewEntry(entryId: string, status: 'approved' | 'rejected') {
    if (busy) return;
    let payrollNote: string | undefined;
    if (status === 'rejected') {
      const note = window.prompt('Optional note for payroll (why this was rejected):');
      if (note === null) return;
      payrollNote = note.trim() || undefined;
    }
    setBusy(entryId);
    try {
      await apiRequest(`/api/time-entries/${entryId}`, {
        method: 'PATCH',
        json: { status, ...(payrollNote ? { payrollNote } : {}) }
      });
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Update failed.');
    } finally {
      setBusy(null);
    }
  }

  function openCreateForm() {
    const defaultTech = selectedTechId !== 'all' ? selectedTechId : technicianOptions[0]?.id || '';
    setEntryForm(emptyEntryForm(defaultTech));
    setError(null);
  }

  async function saveEntryForm() {
    if (!entryForm || formSaving) return;

    const hours = Number(entryForm.hours);
    if (!entryForm.technicianId) {
      setError('Select a technician.');
      return;
    }
    if (!entryForm.date) {
      setError('Date is required.');
      return;
    }
    if (Number.isNaN(hours) || hours < 0 || hours > 24) {
      setError('Enter valid hours between 0 and 24.');
      return;
    }

    setFormSaving(true);
    setError(null);
    try {
      const site = entryForm.site.trim();
      const notes = entryForm.notes.trim();

      if (entryForm.mode === 'create') {
        await apiRequest('/api/time-entries', {
          method: 'POST',
          json: {
            technicianId: entryForm.technicianId,
            date: entryForm.date,
            hours,
            workOrderTitle: site || 'General',
            siteName: site || undefined,
            siteAddress: site || undefined,
            notes: notes || undefined
          }
        });
      } else if (entryForm.entryId) {
        const json: Record<string, unknown> = {
          date: entryForm.date,
          hours,
          workOrderTitle: site || undefined,
          siteName: site || undefined,
          siteAddress: site || undefined,
          notes
        };
        if (entryForm.kind === 'clock') {
          if (entryForm.clockInAt) json.clockInAt = new Date(entryForm.clockInAt).toISOString();
          if (entryForm.clockOutAt) json.clockOutAt = new Date(entryForm.clockOutAt).toISOString();
        }
        await apiRequest(`/api/time-entries/${entryForm.entryId}`, {
          method: 'PATCH',
          json
        });
      }

      setEntryForm(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Could not save entry.');
    } finally {
      setFormSaving(false);
    }
  }

  async function approveWeek() {
    if (busy || !summary) return;
    setBusy('bulk');
    try {
      await apiRequest('/api/time-entries/payroll', {
        method: 'POST',
        json: {
          action: 'approve',
          from: summary.period.from.slice(0, 10),
          to: summary.period.to.slice(0, 10),
          ...(selectedTechId !== 'all' ? { technicianId: selectedTechId } : {})
        }
      });
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Bulk approve failed.');
    } finally {
      setBusy(null);
    }
  }

  async function exportApprovedCsv() {
    if (!summary || busy) return;
    const from = summary.period.from.slice(0, 10);
    const to = summary.period.to.slice(0, 10);
    const techQuery = selectedTechId !== 'all' ? `&technicianId=${encodeURIComponent(selectedTechId)}` : '';
    setBusy('export');
    setError(null);
    setExportSuccess(null);
    try {
      await downloadApiFile(
        `/api/payroll/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&markExported=true${techQuery}`,
        `payroll-hours-${from}-to-${to}.csv`
      );
      setExportSuccess('Approved hours exported.');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Export failed.');
    } finally {
      setBusy(null);
    }
  }

  async function syncToPayroll() {
    if (!summary || busy) return;
    const from = summary.period.from.slice(0, 10);
    const to = summary.period.to.slice(0, 10);
    setBusy('sync');
    setError(null);
    setExportSuccess(null);
    try {
      const res = await apiRequest<{
        success: boolean;
        provider: string;
        synced: number;
        failed: number;
      }>('/api/payroll/sync', {
        method: 'POST',
        json: {
          from,
          to,
          ...(selectedTechId !== 'all' ? { technicianId: selectedTechId } : {})
        }
      });
      if (res.failed > 0) {
        setError(`${res.failed} entries failed to sync. Check payroll employee IDs in Team settings.`);
      } else if (res.synced === 0) {
        setExportSuccess('No new approved hours to sync.');
      } else {
        setExportSuccess(
          res.provider && res.provider !== 'csv'
            ? `Synced ${res.synced} entries to payroll.`
            : `Marked ${res.synced} entries as exported.`
        );
      }
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Payroll sync failed.');
    } finally {
      setBusy(null);
    }
  }

  const technicianOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const tech of roster) {
      map.set(String(tech._id), tech.name || 'Technician');
    }
    for (const row of summary?.technicians || []) {
      if (!map.has(row.technicianId)) {
        map.set(row.technicianId, row.technicianName);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roster, summary?.technicians]);

  const filterTabs: { key: ApprovalFilter; label: string; count?: number }[] = [
    { key: 'pending', label: 'Needs review', count: pendingEntries.length },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All entries' }
  ];

  return (
    <div className="hours-approval-page">
      <section className="quote-card hours-approval-hero">
        <p className="hours-approval-eyebrow">Payroll</p>
        <h2 className="hours-approval-title">Hours approval</h2>
        <p className="settings-hint">
          Review technician clock punches for this pay week, approve hours for payroll, then export a CSV or sync to
          your connected payroll provider. Pay weeks run Monday–Sunday.
        </p>
        <div className="hours-approval-hero-actions">
          <button type="button" className="dash-btn dash-btn-ghost" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            ← Prev week
          </button>
          <span className="hours-week-pill">{formatWeekLabel(weekStart)}</span>
          <button type="button" className="dash-btn dash-btn-ghost" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            Next week →
          </button>
        </div>
      </section>

      {error ? <div className="banner banner-error">{error}</div> : null}
      {exportSuccess ? <div className="banner banner-success">{exportSuccess}</div> : null}

      {technicianOptions.length === 0 ? (
        <section className="quote-card hours-empty-roster">
          <h3 className="hours-table-title">No technicians on your team</h3>
          <p className="settings-hint">
            Hours approval tracks people with the <strong>Technician</strong> role. Admins are not listed here unless they
            clock in using Field mode (their hours still appear in the table once logged).
          </p>
          <p className="settings-hint">
            Invite a technician from Team settings, or turn on Field mode under Security if you want to clock in yourself.
          </p>
          <Link to="/admin/settings?tab=team" className="btn-primary team-hours-link-btn">
            Go to Team → Invite technician
          </Link>
        </section>
      ) : null}

      <div className="hours-kpi-row">
        <div className="hours-kpi">
          <span className="hours-kpi-label">Total hours</span>
          <strong>{formatHours(totals.totalHours)}</strong>
        </div>
        <div className="hours-kpi hours-kpi--accent">
          <span className="hours-kpi-label">Pending review</span>
          <strong>{formatHours(totals.pendingHours)}</strong>
        </div>
        <div className="hours-kpi hours-kpi--ok">
          <span className="hours-kpi-label">Approved</span>
          <strong>{formatHours(totals.approvedHours)}</strong>
        </div>
      </div>

      <section className="quote-card hours-approval-panel">
        <div className="hours-toolbar">
          <label className="hours-filter">
            <span>Technician</span>
            <select value={selectedTechId} onChange={(e) => setSelectedTechId(e.target.value)}>
              <option value="all">All technicians</option>
              {technicianOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <div className="hours-toolbar-actions">
            <button
              type="button"
              className="dash-btn dash-btn-ghost"
              disabled={technicianOptions.length === 0 || Boolean(entryForm)}
              onClick={openCreateForm}
            >
              + Add manual entry
            </button>
            <button
              type="button"
              className="dash-btn dash-btn-ghost"
              disabled={!summary || busy === 'export' || busy === 'sync'}
              onClick={() => void exportApprovedCsv()}
            >
              {busy === 'export' ? 'Exporting…' : 'Export approved CSV'}
            </button>
            {payrollSettings?.connectedConnector && payrollSettings.quickbooks.connected ? (
              <button
                type="button"
                className="dash-btn dash-btn-ghost"
                disabled={!summary || busy === 'sync' || busy === 'export'}
                onClick={() => void syncToPayroll()}
              >
                {busy === 'sync' ? 'Syncing…' : 'Sync to payroll'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary"
              disabled={!summary || busy === 'bulk' || pendingEntries.length === 0}
              onClick={() => void approveWeek()}
            >
              {busy === 'bulk' ? 'Approving…' : `Approve all closed (${pendingEntries.length})`}
            </button>
          </div>
        </div>

        <div className="hours-filter-tabs" role="tablist" aria-label="Approval filter">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={approvalFilter === tab.key}
              className={`hours-filter-tab${approvalFilter === tab.key ? ' is-active' : ''}`}
              onClick={() => setApprovalFilter(tab.key)}
            >
              {tab.label}
              {typeof tab.count === 'number' && tab.count > 0 ? (
                <span className="hours-filter-badge">{tab.count}</span>
              ) : null}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="settings-invite-skeleton" aria-hidden>
            <div className="settings-invite-skeleton-row" />
            <div className="settings-invite-skeleton-row" />
          </div>
        ) : approvalFilter === 'pending' && pendingEntries.length > 0 ? (
          <div className="hours-queue">
            <h3 className="hours-queue-title">Approval queue</h3>
            <div className="hours-queue-list">
              {pendingEntries.map((entry) => {
                const inUrl = mapsUrl(entry.clockInLocation);
                return (
                  <article key={entry._id} className="hours-queue-card">
                    <div className="hours-queue-card-head">
                      <div>
                        <div className="hours-queue-name-row">
                          <strong>{entry.technicianName || 'Technician'}</strong>
                          {entry.kind === 'manual' ? <ManualEntryBadge /> : null}
                        </div>
                        <p className="hours-queue-meta">
                          {formatWhen(entry.clockInAt || entry.date)}
                          {entry.clockOutAt ? ` → ${formatWhen(entry.clockOutAt)}` : ''}
                        </p>
                      </div>
                      <div className="hours-queue-hours">{formatHours(entryHours(entry))} hrs</div>
                    </div>
                    <p className="hours-queue-site">{entry.workOrderTitle || entry.siteAddress || 'No job linked'}</p>
                    {inUrl ? (
                      <a className="hours-queue-map" href={inUrl} target="_blank" rel="noreferrer">
                        📍 {locationLabel(entry.clockInLocation, entry.siteAddress)}
                      </a>
                    ) : (
                      <p className="hours-queue-map hours-queue-map--muted">📍 {locationLabel(entry.clockInLocation, entry.siteAddress)}</p>
                    )}
                    <div className="hours-queue-actions">
                      <button
                        type="button"
                        className="dash-btn dash-btn-ghost"
                        disabled={busy === entry._id || Boolean(entryForm)}
                        onClick={() => setEntryForm(entryToForm(entry))}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busy === entry._id}
                        onClick={() => void reviewEntry(entry._id, 'approved')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="dash-btn dash-btn-ghost"
                        disabled={busy === entry._id}
                        onClick={() => void reviewEntry(entry._id, 'rejected')}
                      >
                        Reject
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="hours-table-wrap">
          <h3 className="hours-table-title">
            {approvalFilter === 'pending' ? 'All pending & open' : 'Time entries'}
          </h3>
          <div className="list-responsive">
            <div className="list-table-pane">
              <table className="settings-table hours-table">
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th>Clock in</th>
                    <th>Clock out</th>
                    <th>Hours</th>
                    <th>Location</th>
                    <th>Job / site</th>
                    <th>Status</th>
                    <th>Payroll</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="hours-table-empty">
                        {approvalFilter === 'pending'
                          ? 'No closed entries waiting for approval this week.'
                          : 'No entries match this filter.'}
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((entry) => {
                      const inUrl = mapsUrl(entry.clockInLocation);
                      const open = isOpenEntry(entry);
                      return (
                        <tr key={entry._id} className={open ? 'hours-row--open' : undefined}>
                          <td>{entry.technicianName || '—'}</td>
                          <td>{formatWhen(entry.clockInAt || entry.date)}</td>
                          <td>{open ? 'Still on clock' : formatWhen(entry.clockOutAt)}</td>
                          <td>
                            <strong>{formatHours(entryHours(entry))}</strong>
                          </td>
                          <td>
                            {inUrl ? (
                              <a href={inUrl} target="_blank" rel="noreferrer" className="hours-map-link">
                                {locationLabel(entry.clockInLocation, entry.siteAddress)}
                              </a>
                            ) : (
                              locationLabel(entry.clockInLocation, entry.siteAddress)
                            )}
                          </td>
                          <td>{entry.workOrderTitle || entry.siteAddress || '—'}</td>
                          <td>
                            <div className="hours-status-cell">
                              <span className={statusClass(entry)}>{statusLabel(entry)}</span>
                              {entry.kind === 'manual' ? <ManualEntryBadge /> : null}
                            </div>
                          </td>
                          <td>
                            <span
                              className={payrollExportClass(entry)}
                              title={entry.payrollExportError || undefined}
                            >
                              {payrollExportLabel(entry)}
                            </span>
                          </td>
                          <td className="hours-row-actions">
                            {!open ? (
                              <button
                                type="button"
                                className="hours-btn hours-btn--edit"
                                disabled={busy === entry._id || Boolean(entryForm)}
                                onClick={() => setEntryForm(entryToForm(entry))}
                              >
                                Edit
                              </button>
                            ) : null}
                            {!open && entry.status !== 'approved' ? (
                              <button
                                type="button"
                                className="hours-btn hours-btn--approve"
                                disabled={busy === entry._id}
                                onClick={() => void reviewEntry(entry._id, 'approved')}
                              >
                                Approve
                              </button>
                            ) : null}
                            {!open && entry.status !== 'rejected' ? (
                              <button
                                type="button"
                                className="hours-btn hours-btn--reject"
                                disabled={busy === entry._id}
                                onClick={() => void reviewEntry(entry._id, 'rejected')}
                              >
                                Reject
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <p className="settings-hint hours-team-link">
        Payroll method and connector setup are in{' '}
        <Link to="/admin/settings?tab=payroll">Payroll settings</Link>. Technician employee IDs are in{' '}
        <Link to="/admin/settings?tab=team">Team settings</Link>.
      </p>

      {entryForm ? (
        <div className="hours-entry-modal-backdrop" onClick={() => !formSaving && setEntryForm(null)}>
          <div
            className="hours-entry-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hours-entry-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hours-entry-modal-head">
              <h3 id="hours-entry-modal-title">
                {entryForm.mode === 'create' ? 'Add manual entry' : 'Edit time entry'}
              </h3>
              <button
                type="button"
                className="hours-entry-modal-close"
                aria-label="Close"
                disabled={formSaving}
                onClick={() => setEntryForm(null)}
              >
                ×
              </button>
            </div>
            <p className="settings-hint hours-entry-modal-hint">
              {entryForm.mode === 'create'
                ? 'Add hours when a technician forgot to clock in or out. Manual entries are flagged for review.'
                : 'Update hours or job details. Approved entries return to Needs review after you save changes.'}
            </p>
            <div className="hours-entry-form">
              <label className="hours-form-field">
                <span>Technician</span>
                <select
                  value={entryForm.technicianId}
                  disabled={entryForm.mode === 'edit' || formSaving}
                  onChange={(e) => setEntryForm((f) => (f ? { ...f, technicianId: e.target.value } : f))}
                >
                  <option value="">Select technician…</option>
                  {technicianOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="hours-form-field">
                <span>Date</span>
                <input
                  type="date"
                  value={entryForm.date}
                  disabled={formSaving}
                  onChange={(e) => setEntryForm((f) => (f ? { ...f, date: e.target.value } : f))}
                />
              </label>
              <label className="hours-form-field">
                <span>Hours</span>
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={0.25}
                  value={entryForm.hours}
                  disabled={formSaving}
                  onChange={(e) => setEntryForm((f) => (f ? { ...f, hours: e.target.value } : f))}
                />
              </label>
              {entryForm.mode === 'edit' && entryForm.kind === 'clock' ? (
                <>
                  <label className="hours-form-field">
                    <span>Clock in</span>
                    <input
                      type="datetime-local"
                      value={entryForm.clockInAt}
                      disabled={formSaving}
                      onChange={(e) => setEntryForm((f) => (f ? { ...f, clockInAt: e.target.value } : f))}
                    />
                  </label>
                  <label className="hours-form-field">
                    <span>Clock out</span>
                    <input
                      type="datetime-local"
                      value={entryForm.clockOutAt}
                      disabled={formSaving}
                      onChange={(e) => setEntryForm((f) => (f ? { ...f, clockOutAt: e.target.value } : f))}
                    />
                  </label>
                </>
              ) : null}
              <label className="hours-form-field">
                <span>Job / site</span>
                <input
                  type="text"
                  placeholder="Site or job name"
                  value={entryForm.site}
                  disabled={formSaving}
                  onChange={(e) => setEntryForm((f) => (f ? { ...f, site: e.target.value } : f))}
                />
              </label>
              <label className="hours-form-field">
                <span>Notes</span>
                <textarea
                  rows={3}
                  placeholder="Optional note for payroll"
                  value={entryForm.notes}
                  disabled={formSaving}
                  onChange={(e) => setEntryForm((f) => (f ? { ...f, notes: e.target.value } : f))}
                />
              </label>
            </div>
            <div className="hours-entry-modal-actions">
              <button
                type="button"
                className="dash-btn dash-btn-ghost"
                disabled={formSaving}
                onClick={() => setEntryForm(null)}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={formSaving} onClick={() => void saveEntryForm()}>
                {formSaving ? 'Saving…' : entryForm.mode === 'create' ? 'Add entry' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
